import type { Candle, CandleInterval } from '../types';

interface MurreyLevels {
  eightEight: number;
  fourEight: number;
  zeroEight: number;
  increment: number;
  absTop: number;
}

export type TraderPro17LevelRole = 'resistance' | 'neutral' | 'support';
export type TraderPro17LevelLookback = 128 | 32 | 16 | 8;

export interface TraderPro17LevelZoneSource {
  lookback: TraderPro17LevelLookback;
  role: TraderPro17LevelRole;
}

export interface TraderPro17LevelZone {
  price: number;
  sources: TraderPro17LevelZoneSource[];
}

interface TraderPro17Params {
  candles: Candle[];
  dailyCandles: Candle[];
  symbol: string;
  interval: CandleInterval;
  enable200Dma?: boolean;
  enableAdvancedTrading?: boolean;
  enableNoobProtection?: boolean;
  rangeScale?: number;
}

export interface TraderPro17Signals {
  validRes100: boolean;
  validRes75a: boolean;
  validRes75b: boolean;
  validNeu100: boolean;
  validNeu75a: boolean;
  validNeu75b: boolean;
  validSup100: boolean;
  validSup75a: boolean;
  validSup75b: boolean;
  validScs: boolean;
  validScs2: boolean;
  validExtTp: boolean;
  validExtTp2: boolean;
}

export interface TraderPro17OverlayResult {
  allowRender: boolean;
  advancedTradingEnabled: boolean;
  currentPrice: number;
  currentHigh: number;
  currentLow: number;
  levels128: MurreyLevels;
  levels32: MurreyLevels;
  levels16: MurreyLevels;
  levels8: MurreyLevels;
  levelZones: TraderPro17LevelZone[];
  plus18: number;
  plus28: number;
  minus18: number;
  minus28: number;
  dma200?: number;
  signals: TraderPro17Signals;
  info: string;
}

const LOOKBACK_128 = 128;
const LOOKBACK_32 = 32;
const LOOKBACK_16 = 16;
const LOOKBACK_8 = 8;

const DEFAULT_ENABLE_200_DMA = false;
const DEFAULT_ENABLE_ADVANCED_TRADING = true;
const DEFAULT_ENABLE_NOOB_PROTECTION = false;
const DEFAULT_RANGE_SCALE = 1;
const MIN_RANGE_SCALE = 0.5;
const MAX_RANGE_SCALE = 1.5;

function toNewestFirst(candles: Candle[]) {
  return [...candles].reverse();
}

function sma(values: number[], length: number): number | undefined {
  if (values.length < length || length <= 0) {
    return undefined;
  }

  let sum = 0;
  for (let index = 0; index < length; index += 1) {
    sum += values[index];
  }
  return sum / length;
}

function lowest(values: number[], lookback: number): number {
  let low = Number.POSITIVE_INFINITY;
  for (let index = 0; index <= lookback && index < values.length; index += 1) {
    if (values[index] < low) {
      low = values[index];
    }
  }
  return low;
}

function highest(values: number[], lookback: number): number {
  let high = Number.NEGATIVE_INFINITY;
  for (let index = 0; index <= lookback && index < values.length; index += 1) {
    if (values[index] > high) {
      high = values[index];
    }
  }
  return high;
}

function murreyLookback(highs: number[], lows: number[], lookback: number): MurreyLevels {
  const logTen = Math.log(10);
  const log8 = Math.log(8);
  const log2 = Math.log(2);

  const vLow = lowest(lows, lookback);
  const vHigh = highest(highs, lookback);
  const vDist = vHigh - vLow;
  const tmpHigh = vLow < 0 ? 0 - vLow : vHigh;
  const tmpLow = vLow < 0 ? 0 - vLow - vDist : vLow;
  const shifted = vLow < 0;

  const safeTmpHigh = Math.max(tmpHigh, 1e-9);
  const sfVarRaw = Math.log(0.4 * safeTmpHigh) / logTen;
  const sfVar = sfVarRaw - Math.floor(sfVarRaw);

  const sr =
    safeTmpHigh > 25
      ? sfVar > 0
        ? Math.exp(logTen * (Math.floor(Math.log(0.4 * safeTmpHigh) / logTen) + 1))
        : Math.exp(logTen * Math.floor(Math.log(0.4 * safeTmpHigh) / logTen))
      : 100 * Math.exp(log8 * Math.floor(Math.log(0.005 * safeTmpHigh) / log8));

  const range = Math.max(safeTmpHigh - tmpLow, 1e-9);
  const nVar1 = Math.log(sr / range) / log8;
  const nVar2 = nVar1 - Math.floor(nVar1);
  const n = nVar1 <= 0 ? 0 : nVar2 === 0 ? Math.floor(nVar1) : Math.floor(nVar1) + 1;

  const si = sr * Math.exp(-n * log8);
  const m = Math.floor((1 / log2) * Math.log(range / si) + 0.0000001);
  const i = Math.round(((safeTmpHigh + tmpLow) * 0.5) / (si * Math.exp((m - 1) * log2)));

  const bot = (i - 1) * si * Math.exp((m - 1) * log2);
  const top = (i + 1) * si * Math.exp((m - 1) * log2);

  const doShift = safeTmpHigh - top > 0.25 * (top - bot) || bot - tmpLow > 0.25 * (top - bot);
  const mm = !doShift ? m : doShift && m < 2 ? m + 1 : 0;
  const nn = !doShift ? n : doShift && m < 2 ? n : n - 1;

  const finalSi = doShift ? sr * Math.exp(-nn * log8) : si;
  const finalI = doShift
    ? Math.round(((safeTmpHigh + tmpLow) * 0.5) / (finalSi * Math.exp((mm - 1) * log2)))
    : i;
  const finalBot = doShift ? (finalI - 1) * finalSi * Math.exp((mm - 1) * log2) : bot;
  const finalTop = doShift ? (finalI + 1) * finalSi * Math.exp((mm - 1) * log2) : top;

  const increment = (finalTop - finalBot) / 8;
  const absTop = shifted ? -(finalBot - 3 * increment) : finalTop + 3 * increment;

  return {
    eightEight: absTop - 3 * increment,
    fourEight: absTop - 7 * increment,
    zeroEight: absTop - 11 * increment,
    increment,
    absTop,
  };
}

function normalizeRangeScale(rangeScale: number | undefined): number {
  if (typeof rangeScale !== 'number' || !Number.isFinite(rangeScale)) {
    return DEFAULT_RANGE_SCALE;
  }
  return Math.min(MAX_RANGE_SCALE, Math.max(MIN_RANGE_SCALE, rangeScale));
}

function scaleAroundCenter(price: number, center: number, rangeScale: number): number {
  return center + (price - center) * rangeScale;
}

function roundToTick(price: number, tick: number): number {
  if (!Number.isFinite(tick) || tick <= 0) {
    return price;
  }
  return Math.round(price / tick) * tick;
}

function buildGroupedPriceScaler(
  prices: number[],
  anchor: number,
  rangeScale: number,
  tick: number,
): (price: number) => number {
  if (rangeScale === DEFAULT_RANGE_SCALE) {
    return (price: number) => price;
  }

  const scaledByKey = new Map<number, number>();
  for (const price of prices) {
    const key = roundToTick(price, tick);
    if (!scaledByKey.has(key)) {
      scaledByKey.set(key, scaleAroundCenter(key, anchor, rangeScale));
    }
  }

  return (price: number) => {
    const key = roundToTick(price, tick);
    const scaled = scaledByKey.get(key);
    if (scaled !== undefined) {
      return scaled;
    }
    return scaleAroundCenter(key, anchor, rangeScale);
  };
}

function scaleMurreyLevels(levels: MurreyLevels, scalePrice: (price: number) => number): MurreyLevels {
  const fourEight = scalePrice(levels.fourEight);
  const eightEight = scalePrice(levels.eightEight);
  const zeroEight = scalePrice(levels.zeroEight);
  const increment = (eightEight - zeroEight) / 8;
  const absTop = eightEight + 3 * increment;

  return {
    eightEight,
    fourEight,
    zeroEight,
    increment,
    absTop,
  };
}

function intervalToMinutes(interval: CandleInterval): number {
  if (interval === '1m') {
    return 1;
  }
  if (interval === '3m') {
    return 3;
  }
  if (interval === '5m') {
    return 5;
  }
  if (interval === '15m') {
    return 15;
  }
  if (interval === '30m') {
    return 30;
  }
  if (interval === '45m') {
    return 45;
  }
  if (interval === '1h') {
    return 60;
  }
  if (interval === '2h') {
    return 120;
  }
  if (interval === '3h') {
    return 180;
  }
  if (interval === '4h') {
    return 240;
  }
  if (interval === '1d') {
    return 1440;
  }
  if (interval === '1w') {
    return 10080;
  }
  if (interval === '1M') {
    return 43200;
  }
  return 43200;
}

function isIntraday(interval: CandleInterval): boolean {
  return intervalToMinutes(interval) < 1440;
}

function inferTickSize(symbol: string, candles: Candle[]): number {
  if (symbol.toUpperCase() === 'XBTUSD') {
    return 0.5;
  }

  let maxPrecision = 0;
  for (let index = 0; index < candles.length && index < 120; index += 1) {
    const candle = candles[index];
    const values = [candle.open, candle.high, candle.low, candle.close];
    for (const value of values) {
      const text = value.toString();
      const decimalIndex = text.indexOf('.');
      if (decimalIndex < 0) {
        continue;
      }
      const precision = text.length - decimalIndex - 1;
      if (precision > maxPrecision) {
        maxPrecision = precision;
      }
    }
  }

  if (maxPrecision <= 0) {
    return 1;
  }

  return 1 / 10 ** maxPrecision;
}

function levelsArray(levels: MurreyLevels): number[] {
  return [levels.eightEight, levels.fourEight, levels.zeroEight];
}

function withinTick(a: number, b: number, tick: number): boolean {
  return Math.abs(a - b) <= tick;
}

function anyEqual(values: number[], target: number, tick: number): boolean {
  for (const value of values) {
    if (withinTick(value, target, tick)) {
      return true;
    }
  }
  return false;
}

function symbolLabel(symbol: string): string {
  return symbol.includes('/') ? symbol : symbol.replace(/([A-Z]{3})$/, '/$1');
}

function buildLevelZones(
  rawLevelSets: Array<{ lookback: TraderPro17LevelLookback; levels: MurreyLevels }>,
  scaledLevelSets: Array<{ lookback: TraderPro17LevelLookback; levels: MurreyLevels }>,
  tick: number,
): TraderPro17LevelZone[] {
  const entries = rawLevelSets.flatMap(({ lookback, levels }, index) => {
    const scaledLevels = scaledLevelSets[index]?.levels;
    if (!scaledLevels) {
      return [];
    }

    return [
      {
        lookback,
        role: 'resistance' as const,
        rawPrice: levels.eightEight,
        price: scaledLevels.eightEight,
      },
      {
        lookback,
        role: 'neutral' as const,
        rawPrice: levels.fourEight,
        price: scaledLevels.fourEight,
      },
      {
        lookback,
        role: 'support' as const,
        rawPrice: levels.zeroEight,
        price: scaledLevels.zeroEight,
      },
    ];
  });

  const clusters: Array<{
    rawCenter: number;
    priceCenter: number;
    entries: typeof entries;
  }> = [];

  const sortedEntries = [...entries].sort((left, right) => right.rawPrice - left.rawPrice);
  for (const entry of sortedEntries) {
    let bestIndex = -1;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (let index = 0; index < clusters.length; index += 1) {
      const cluster = clusters[index];
      const distance = Math.abs(cluster.rawCenter - entry.rawPrice);
      if (distance <= tick && distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    }

    if (bestIndex < 0) {
      clusters.push({
        rawCenter: entry.rawPrice,
        priceCenter: entry.price,
        entries: [entry],
      });
      continue;
    }

    const cluster = clusters[bestIndex];
    cluster.entries.push(entry);
    const count = cluster.entries.length;
    cluster.rawCenter = (cluster.rawCenter * (count - 1) + entry.rawPrice) / count;
    cluster.priceCenter = (cluster.priceCenter * (count - 1) + entry.price) / count;
  }

  const lookbackOrder: TraderPro17LevelLookback[] = [128, 32, 16, 8];

  return clusters
    .map((cluster) => ({
      price: cluster.priceCenter,
      sources: [...cluster.entries]
        .sort((left, right) => lookbackOrder.indexOf(left.lookback) - lookbackOrder.indexOf(right.lookback))
        .map(({ lookback, role }) => ({ lookback, role })),
    }))
    .sort((left, right) => right.price - left.price);
}

export function computeTraderPro17Overlay({
  candles,
  dailyCandles,
  symbol,
  interval,
  enable200Dma = DEFAULT_ENABLE_200_DMA,
  enableAdvancedTrading = DEFAULT_ENABLE_ADVANCED_TRADING,
  enableNoobProtection = DEFAULT_ENABLE_NOOB_PROTECTION,
  rangeScale = DEFAULT_RANGE_SCALE,
}: TraderPro17Params): TraderPro17OverlayResult | null {
  if (candles.length < LOOKBACK_128 + 1) {
    return null;
  }

  const newest = toNewestFirst(candles);
  const currentCandle = newest[0];
  const highs = newest.map((candle) => candle.high);
  const lows = newest.map((candle) => candle.low);

  const effectiveRangeScale = normalizeRangeScale(rangeScale);

  const rawLevels128 = murreyLookback(highs, lows, LOOKBACK_128);
  const rawLevels32 = murreyLookback(highs, lows, LOOKBACK_32);
  const rawLevels16 = murreyLookback(highs, lows, LOOKBACK_16);
  const rawLevels8 = murreyLookback(highs, lows, LOOKBACK_8);

  const rawMinus18 = rawLevels128.absTop - 12 * rawLevels128.increment;
  const rawMinus28 = rawLevels128.absTop - 13 * rawLevels128.increment;

  const tick = inferTickSize(symbol, newest);
  const l0 = levelsArray(rawLevels128);
  const l1 = levelsArray(rawLevels32);
  const l2 = levelsArray(rawLevels16);
  const l3 = levelsArray(rawLevels8);

  const allResistance =
    anyEqual(l1, rawLevels128.eightEight, tick) &&
    anyEqual(l2, rawLevels128.eightEight, tick) &&
    anyEqual(l3, rawLevels128.eightEight, tick);
  const resistance75a =
    anyEqual(l1, rawLevels128.eightEight, tick) &&
    anyEqual(l2, rawLevels128.eightEight, tick) &&
    !withinTick(rawLevels8.eightEight, rawLevels128.eightEight, tick) &&
    !withinTick(rawLevels8.fourEight, rawLevels128.eightEight, tick);
  const resistance75b =
    anyEqual(l2, rawLevels32.eightEight, tick) &&
    anyEqual(l3, rawLevels32.eightEight, tick) &&
    !withinTick(rawLevels128.eightEight, rawLevels32.eightEight, tick) &&
    !withinTick(rawLevels128.fourEight, rawLevels32.eightEight, tick);

  const allNeutral =
    anyEqual(l1, rawLevels128.fourEight, tick) &&
    anyEqual(l2, rawLevels128.fourEight, tick) &&
    anyEqual(l3, rawLevels128.fourEight, tick);
  const neutral75a =
    anyEqual(l1, rawLevels128.fourEight, tick) &&
    anyEqual(l2, rawLevels128.fourEight, tick) &&
    !anyEqual(l3, rawLevels128.fourEight, tick);
  const neutral75b =
    anyEqual(l2, rawLevels32.fourEight, tick) &&
    anyEqual(l3, rawLevels32.fourEight, tick) &&
    !anyEqual(l0, rawLevels32.fourEight, tick);

  const allSupport =
    anyEqual(l1, rawLevels128.zeroEight, tick) &&
    anyEqual(l2, rawLevels128.zeroEight, tick) &&
    anyEqual(l3, rawLevels128.zeroEight, tick);
  const support75a =
    anyEqual(l1, rawLevels128.zeroEight, tick) &&
    anyEqual(l2, rawLevels128.zeroEight, tick) &&
    !withinTick(rawLevels8.zeroEight, rawLevels128.zeroEight, tick) &&
    !withinTick(rawLevels8.fourEight, rawLevels128.zeroEight, tick);
  const support75b =
    anyEqual(l2, rawLevels32.zeroEight, tick) &&
    anyEqual(l3, rawLevels32.zeroEight, tick) &&
    !withinTick(rawLevels128.zeroEight, rawLevels32.zeroEight, tick) &&
    !withinTick(rawLevels128.fourEight, rawLevels32.zeroEight, tick);

  const scs =
    withinTick(rawMinus18, rawLevels32.zeroEight, tick) &&
    withinTick(rawLevels16.zeroEight, rawLevels8.zeroEight, tick) &&
    withinTick(rawMinus18, rawLevels8.zeroEight, tick);
  const scs2 =
    withinTick(rawMinus28, rawLevels32.zeroEight, tick) &&
    withinTick(rawLevels16.zeroEight, rawLevels8.zeroEight, tick) &&
    withinTick(rawMinus28, rawLevels8.zeroEight, tick);

  const validExtTp = Math.round(rawLevels128.eightEight) > Math.round(rawLevels32.eightEight);
  const validExtTp2 = Math.round(rawLevels32.eightEight) > Math.round(rawLevels128.eightEight);

  const scaleAnchor = rawLevels128.fourEight;
  const groupedScalePrice = buildGroupedPriceScaler(
    [
      rawLevels128.eightEight,
      rawLevels128.fourEight,
      rawLevels128.zeroEight,
      rawLevels32.eightEight,
      rawLevels32.fourEight,
      rawLevels32.zeroEight,
      rawLevels16.eightEight,
      rawLevels16.fourEight,
      rawLevels16.zeroEight,
      rawLevels8.eightEight,
      rawLevels8.fourEight,
      rawLevels8.zeroEight,
    ],
    scaleAnchor,
    effectiveRangeScale,
    tick,
  );
  const levels128 = scaleMurreyLevels(rawLevels128, groupedScalePrice);
  const levels32 = scaleMurreyLevels(rawLevels32, groupedScalePrice);
  const levels16 = scaleMurreyLevels(rawLevels16, groupedScalePrice);
  const levels8 = scaleMurreyLevels(rawLevels8, groupedScalePrice);
  const levelZones = buildLevelZones(
    [
      { lookback: 128, levels: rawLevels128 },
      { lookback: 32, levels: rawLevels32 },
      { lookback: 16, levels: rawLevels16 },
      { lookback: 8, levels: rawLevels8 },
    ],
    [
      { lookback: 128, levels: levels128 },
      { lookback: 32, levels: levels32 },
      { lookback: 16, levels: levels16 },
      { lookback: 8, levels: levels8 },
    ],
    tick,
  );

  const minus18 = levels128.absTop - 12 * levels128.increment;
  const minus28 = levels128.absTop - 13 * levels128.increment;
  const plus28 = levels128.absTop - levels128.increment;
  const plus18 = levels128.absTop - 2 * levels128.increment;

  const allowRender =
    !(enableNoobProtection && isIntraday(interval) && intervalToMinutes(interval) < 240);

  const dailyNewest = toNewestFirst(dailyCandles);
  const dma200 = enable200Dma ? sma(dailyNewest.map((candle) => candle.close), 200) : undefined;

  const notes: string[] = [];
  if (!allowRender) {
    notes.push('Noob protection active');
  }

  if (allResistance) {
    notes.push('Sell 100%');
  } else if (resistance75a || resistance75b) {
    notes.push('Sell 75%');
  }

  if (allNeutral) {
    notes.push('Neutral 100%');
  } else if (neutral75a || neutral75b) {
    notes.push('Neutral 75%');
  }

  if (allSupport) {
    notes.push('Buy 100%');
  } else if (support75a || support75b) {
    notes.push('Buy 75%');
  }

  if (scs) {
    notes.push('Strong Buy zone');
  }
  if (scs2) {
    notes.push('Very Strong Buy zone');
  }

  if (enableAdvancedTrading && (validExtTp || validExtTp2)) {
    notes.push('TraderPro Target');
  }

  const info = `Trader Pro 1.7 ${symbolLabel(symbol)}: ${notes.length ? notes.join(' | ') : 'No active alignments'}`;

  return {
    allowRender,
    advancedTradingEnabled: enableAdvancedTrading,
    currentPrice: currentCandle.close,
    currentHigh: currentCandle.high,
    currentLow: currentCandle.low,
    levels128,
    levels32,
    levels16,
    levels8,
    levelZones,
    plus18,
    plus28,
    minus18,
    minus28,
    dma200,
    signals: {
      validRes100: allResistance,
      validRes75a: resistance75a,
      validRes75b: resistance75b,
      validNeu100: allNeutral,
      validNeu75a: neutral75a,
      validNeu75b: neutral75b,
      validSup100: allSupport,
      validSup75a: support75a,
      validSup75b: support75b,
      validScs: scs,
      validScs2: scs2,
      validExtTp,
      validExtTp2,
    },
    info,
  };
}
